import { Snackbar, SnackbarOrigin } from "@mui/material";
import Alert from "@mui/material/Alert";
import React, { createContext, useCallback, useContext, useState } from "react";
import { LazyLoadImage } from "react-lazy-load-image-component";

type ToastOptions = {
  // timeout: number;
  origin?: SnackbarOrigin;
  prefixIcon?: string;
};

type ToastSuccess = (message: string, options?: ToastOptions) => void;
type ToastError = (message: string, options?: ToastOptions) => void;
type ToastWarning = (message: string, options?: ToastOptions) => void;

type ToastContextValue = {
  success: ToastSuccess;
  error: ToastError;
  warning: ToastWarning;
};

export const ToastContext = createContext<ToastContextValue>({
  success: () => {},
  error: () => {},
  warning: () => {},
});

type ToastMessage = {
  id: string;
  type: "success" | "error" | "warning";
  message: string;
  options: ToastOptions;
  open: boolean;
};

const defaultOptions: ToastOptions = {
  // timeout: 5000,
  origin: {
    horizontal: "center",
    vertical: "top",
  },
};

const alertTheme = {
  borderRadius: "12px",
  boxShadow: "0 2px 8px 0 rgba(0, 0, 0, 0.50)",
  font: "Source Sans Pro",
  fontSize: "16px",
  lineHeight: "24px",
  alignItems: "center",
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [messages, setMessages] = useState<ToastMessage[]>([]);

  const success: ToastSuccess = useCallback(
    (message, options = defaultOptions) => {
      const optionsWithDefaults = { ...defaultOptions, ...options };
      const newMessage: ToastMessage = {
        id: Date.now().toString(),
        type: "success",
        message,
        options: optionsWithDefaults,
        open: true,
      };
      setMessages((prevMessages) => [...prevMessages, newMessage]);
    },
    []
  );

  const error: ToastError = useCallback((message, options = defaultOptions) => {
    const optionsWithDefaults = { ...defaultOptions, ...options };
    const newMessage: ToastMessage = {
      id: Date.now().toString(),
      type: "error",
      message,
      options: optionsWithDefaults,
      open: true,
    };
    setMessages((prevMessages) => [...prevMessages, newMessage]);
  }, []);

  const warning: ToastError = useCallback(
    (message, options = defaultOptions) => {
      const optionsWithDefaults = { ...defaultOptions, ...options };
      const newMessage: ToastMessage = {
        id: Date.now().toString(),
        type: "warning",
        message,
        options: optionsWithDefaults,
        open: true,
      };
      setMessages((prevMessages) => [...prevMessages, newMessage]);
    },
    []
  );

  const onClose = (messageId: string) => {
    const filteredMessages = messages.filter(
      (message) => message.id !== messageId
    );
    setMessages([...filteredMessages]);
  };

  return (
    <ToastContext.Provider value={{ success, error, warning }}>
      {children}
      {messages.map((message) => {
        const PrefixIcon = message.options?.prefixIcon;
        return (
          <Snackbar
            key={message.id}
            open={message.open}
            // autoHideDuration={message.options.timeout}
            onClose={() => onClose(message.id)}
            anchorOrigin={message.options.origin}
          >
            <Alert
              variant="filled"
              severity={message.type}
              onClose={() => onClose(message.id)}
              {...(PrefixIcon && {
                icon: <LazyLoadImage src={PrefixIcon} height={20} />,
              })}
              sx={{
                ...alertTheme,
                ...(message.type === "success" && { bgcolor: "#0A5" }),
                ...(message.type === "warning" && { bgcolor: "#FF8900" }),
              }}
            >
              {message.message}
            </Alert>
          </Snackbar>
        );
      })}
    </ToastContext.Provider>
  );
};

export const ToastConsumer = ToastContext.Consumer;

export const useToast = () => useContext(ToastContext);
